import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Annotation, TextAnnotation } from './types/AnnotationTypes';

interface AnnotationRendererProps {
  annotations: Annotation[];
  scale?: number;
  zoom?: number;
  currentTool?: string | null;
  onSelectAnnotation?: (id: string) => void;
  selectedAnnotationId?: string | null;
  onMoveAnnotation?: (id: string, newX: number, newY: number) => void;
  onStartDrag?: (id: string, offset: { x: number; y: number }) => void;
  onEndDrag?: (id: string, startX: number, startY: number, finalX: number, finalY: number) => void;
  onEditAnnotation?: (annotation: Annotation) => void;
  onDeleteAnnotation?: (annotation: Annotation) => void;
}

const AnnotationRenderer: React.FC<AnnotationRendererProps & { isDrawingHighlight?: boolean, isDrawingShape?: boolean, isDrawingFreehand?: boolean }> = ({ 
  annotations, 
  scale = 1,
  zoom = 1,
  currentTool,
  onSelectAnnotation,
  selectedAnnotationId,
  onMoveAnnotation,
  onStartDrag,
  onEndDrag,
  onEditAnnotation,
  onDeleteAnnotation,
  isDrawingHighlight = false,
  isDrawingShape = false,
  isDrawingFreehand = false
}) => {
  // Remove duplicate annotations based on ID to prevent rendering issues
  const uniqueAnnotations = React.useMemo(() => {
    const seen = new Set<string>();
    return annotations.filter(annotation => {
      const id = (annotation as any).id;
      const x = (annotation as any).x;
      const y = (annotation as any).y;
      const type = (annotation as any).type;
      
      // Check for duplicates
      if (seen.has(id)) {
        console.warn('🚨 Duplicate annotation detected:', id);
        return false;
      }
      
      // Check for valid coordinates
      if (typeof x !== 'number' || typeof y !== 'number') {
        console.warn('🚨 Invalid coordinates for annotation:', id, x, y);
        return false;
      }
      
      // Check for valid type
      if (!type) {
        console.warn('🚨 Missing type for annotation:', id);
        return false;
      }
      
      seen.add(id);
      return true;
    });
  }, [annotations]);

  // Estado local para drag
  const dragState = React.useRef<{ id: string | null; offset: { x: number; y: number } | null; start: { x: number; y: number } | null }>({ id: null, offset: null, start: null });

  // Helper function to create tooltip content with metadata
  const createTooltipContent = (annotation: Annotation) => {
    const formatDate = (date: Date) => {
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return (
      <Box sx={{ p: 1 }}>
        {(annotation as any).note && (
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {(annotation as any).note}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
          <strong>Author:</strong> {annotation.createdBy || 'Unknown'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
          <strong>Created:</strong> {formatDate(annotation.createdAt)}
        </Typography>
      </Box>
    );
  };

  const handleMouseDown = (e: React.MouseEvent, annotation: Annotation) => {
    if (currentTool !== 'move') return;
    if (onSelectAnnotation) onSelectAnnotation(annotation.id);
    const startX = e.clientX;
    const startY = e.clientY;
    dragState.current = {
      id: annotation.id,
      offset: { x: startX - annotation.x * scale * zoom, y: startY - annotation.y * scale * zoom },
      start: { x: annotation.x, y: annotation.y }
    };
    if (onStartDrag) onStartDrag(annotation.id, dragState.current.offset!);
    window.addEventListener('mousemove', handleMouseMove as any);
    window.addEventListener('mouseup', handleMouseUp as any);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragState.current.id || !onMoveAnnotation) return;
    const newX = (e.clientX - dragState.current.offset!.x) / (scale * zoom);
    const newY = (e.clientY - dragState.current.offset!.y) / (scale * zoom);
    onMoveAnnotation(dragState.current.id, newX, newY);
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (dragState.current.id && onEndDrag) {
      const finalX = (e.clientX - dragState.current.offset!.x) / (scale * zoom);
      const finalY = (e.clientY - dragState.current.offset!.y) / (scale * zoom);
      const start = dragState.current.start!;
      onEndDrag(dragState.current.id, start.x, start.y, finalX, finalY);
    }
    dragState.current = { id: null, offset: null, start: null };
    window.removeEventListener('mousemove', handleMouseMove as any);
    window.removeEventListener('mouseup', handleMouseUp as any);
  };

  const renderTextAnnotation = (annotation: TextAnnotation) => {
    const isSelected = selectedAnnotationId === annotation.id;
    return (
      <Box
        key={annotation.id}
        sx={{
          position: 'absolute',
          left: annotation.x * scale * zoom,
          top: annotation.y * scale * zoom,
          zIndex: 100,
          cursor: currentTool === 'move' ? (isSelected ? 'grabbing' : 'grab') : (currentTool === 'text' ? 'text' : 'default'),
          border: isSelected && currentTool === 'move' ? '2px solid #1976d2' : 'none',
          background: isSelected && currentTool === 'move' ? 'rgba(25, 118, 210, 0.08)' : 'none',
          pointerEvents: currentTool === 'move' || currentTool === 'text' ? 'auto' : 'none',
        }}
        onMouseDown={currentTool === 'move' ? (e) => handleMouseDown(e, annotation) : undefined}
        onClick={currentTool === 'text' ? (e) => { e.stopPropagation(); if (onEditAnnotation) onEditAnnotation(annotation); } : undefined}
      >
        <Typography
          variant="body2"
          sx={{
            color: annotation.style.color,
            fontSize: annotation.style.fontSize * scale * zoom,
            fontFamily: annotation.style.fontFamily,
            fontWeight: 'normal',
            textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          {annotation.text}
        </Typography>
      </Box>
    );
  };

  const renderHighlight = (annotation: any) => {
    const isSelected = selectedAnnotationId === annotation.id;
    return (
      <Tooltip
        key={annotation.id}
        title={createTooltipContent(annotation)}
        placement="top"
        arrow
        disableInteractive={false}
        slotProps={{
          popper: {
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: '#fff',
                color: '#000',
                boxShadow: 2,
                fontSize: 14,
                border: '1px solid #ccc',
                maxWidth: 300,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: annotation.x * scale * zoom,
            top: annotation.y * scale * zoom,
            width: annotation.width * scale * zoom,
            height: annotation.height * scale * zoom,
            backgroundColor: annotation.style.color,
            opacity: annotation.opacity ?? 0.6,
            borderRadius: '4px',
            pointerEvents: isDrawingHighlight ? 'none' : 'auto',
            zIndex: 90,
            cursor: currentTool === 'move' ? (isSelected ? 'grabbing' : 'grab') : 'pointer',
            border: isSelected && currentTool === 'move' ? `2px solid ${annotation.style.color}` : 'none',
            transition: 'box-shadow 0.2s',
            boxShadow: isSelected && currentTool === 'move' ? '0 0 0 2px #0002' : 'none',
          }}
          onMouseDown={currentTool === 'move' ? (e) => handleMouseDown(e, annotation) : undefined}
          onClick={!isDrawingHighlight ? (e) => { e.stopPropagation(); if (onEditAnnotation) onEditAnnotation(annotation); } : undefined}
        />
      </Tooltip>
    );
  };

  const renderShape = (annotation: any) => {
    const isSelected = selectedAnnotationId === annotation.id;
    let borderStyle: 'solid' | 'dashed' | 'dotted' = 'solid';
    if (annotation.style.lineType === 'dashed') {
      borderStyle = 'dashed';
    } else if (annotation.style.lineType === 'dotted') {
      borderStyle = 'dotted';
    }
    const style = {
      position: 'absolute',
      left: annotation.x * scale * zoom,
      top: annotation.y * scale * zoom,
      width: annotation.width * scale * zoom,
      height: annotation.height * scale * zoom,
      border: `${annotation.style.thickness || 2}px ${borderStyle} ${annotation.style.color}`,
      borderRadius: annotation.shapeType === 'oval' ? '50%' : '4px',
      pointerEvents: 'auto',
      zIndex: 91,
      cursor: currentTool === 'move' ? (isSelected ? 'grabbing' : 'grab') : 'pointer',
      background: 'transparent',
      boxSizing: 'border-box' as const,
      boxShadow: isSelected && currentTool === 'move' ? '0 0 0 2px #0002' : 'none',
      borderStyle,
    };
    return (
      <Tooltip
        key={annotation.id}
        title={createTooltipContent(annotation)}
        placement="top"
        arrow
        disableInteractive={false}
        slotProps={{
          popper: {
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: '#fff',
                color: '#000',
                boxShadow: 2,
                fontSize: 14,
                border: '1px solid #ccc',
                maxWidth: 300,
              },
            },
          },
        }}
      >
        <Box
          sx={style}
          onMouseDown={currentTool === 'move' ? (e: React.MouseEvent) => handleMouseDown(e, annotation) : undefined}
          onClick={!isDrawingShape ? (e: React.MouseEvent) => { e.stopPropagation(); if (onEditAnnotation) onEditAnnotation(annotation); } : undefined}
        />
      </Tooltip>
    );
  };

  const renderFreehand = (annotation: any) => {
    const isSelected = selectedAnnotationId === annotation.id;
    
    // Calcular los límites del trazo
    const minX = Math.min(...annotation.points.map((p: any) => p.x));
    const maxX = Math.max(...annotation.points.map((p: any) => p.x));
    const minY = Math.min(...annotation.points.map((p: any) => p.y));
    const maxY = Math.max(...annotation.points.map((p: any) => p.y));
    const width = maxX - minX;
    const height = maxY - minY;
    
    return (
      <Tooltip
        key={annotation.id}
        title={createTooltipContent(annotation)}
        placement="top"
        arrow
        disableInteractive={false}
        slotProps={{
          popper: {
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: '#fff',
                color: '#000',
                boxShadow: 2,
                fontSize: 14,
                border: '1px solid #ccc',
                maxWidth: 300,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: (annotation.x - 2) * scale * zoom,
            top: (annotation.y - 2) * scale * zoom,
            width: (width + 4) * scale * zoom,
            height: (height + 4) * scale * zoom,
            pointerEvents: isDrawingFreehand ? 'none' : 'auto',
            zIndex: 92,
            cursor: currentTool === 'move' ? (isSelected ? 'grabbing' : 'grab') : 'pointer',
          }}
          onMouseDown={currentTool === 'move' ? (e: React.MouseEvent) => handleMouseDown(e, annotation) : undefined}
          onClick={!isDrawingFreehand ? (e: React.MouseEvent) => { e.stopPropagation(); if (onEditAnnotation) onEditAnnotation(annotation); } : undefined}
        >
          <svg
            style={{
              position: 'absolute',
              top: 2 * scale * zoom,
              left: 2 * scale * zoom,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <path
              d={`M ${annotation.points.map((p: any) => `${(p.x - annotation.x) * scale * zoom} ${(p.y - annotation.y) * scale * zoom}`).join(' L ')}`}
              stroke={annotation.style.color}
              strokeWidth={annotation.style.thickness}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                filter: isSelected && currentTool === 'move' ? 'drop-shadow(0 0 4px rgba(0,0,0,0.3))' : 'none',
              }}
            />
          </svg>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      {uniqueAnnotations.map((annotation) => {
        // Ensure each annotation has a unique key to prevent React rendering issues
        const key = `${annotation.id}-${annotation.type}-${annotation.x}-${annotation.y}`;
        
        switch (annotation.type) {
          case 'text':
            return <React.Fragment key={key}>{renderTextAnnotation(annotation as TextAnnotation)}</React.Fragment>;
          case 'highlight':
            return <React.Fragment key={key}>{renderHighlight(annotation)}</React.Fragment>;
          case 'shapes':
            return <React.Fragment key={key}>{renderShape(annotation)}</React.Fragment>;
          case 'freehand':
            return <React.Fragment key={key}>{renderFreehand(annotation)}</React.Fragment>;
          // TODO: Add other annotation types
          default:
            return null;
        }
      })}
    </Box>
  );
};

export default AnnotationRenderer; 